'use client'

/* ---------------------------------
BuonInvitoEvents
--------------------------------- */

import React, { ReactElement, useEffect, useState } from 'react'
import {
  Button,
  InfoBanner,
  OpeningTitle,
  Section,
  Title,
} from '@dynamo-caffeina-dev/airc-ui-kit'
import { SectionBackground } from '@/types'
import Spinner from '@/components/atoms/Spinner'
import { BuonInvitoEventsModel } from '@/models/BuonInvitoEvents'
import { NormalizedEventType } from '@/types/NormalizedEventType'
import DOMPurify from 'isomorphic-dompurify'
import EventCard from '@/components/molecules/EventCard'
import Pagination from '@/components/molecules/Pagination'
import { EventTypeEnum } from '@/helpers/events'
import {
  isValidSearchInputRealtime,
  validateSearchInput,
  VALIDATION_MESSAGES,
} from '@/helpers/searchValidation'
import { PaginationType } from '@/types/PaginationType'

type OwnProps = {
  props: ReturnType<typeof BuonInvitoEventsModel>['props']
  modelledContent: ReturnType<typeof BuonInvitoEventsModel>['modelledContent']
}

export default function BuonInvitoEvents({
  props,
  modelledContent: extractedContent,
}: OwnProps): ReactElement {
  const { openingTitle, title: _title, config } = extractedContent ?? {}
  const { content: titleContent, ...title } = _title ?? {}
  const { bg } = props ?? {}

  const [searchInput, setSearchInput] = useState('')
  const [searchError, setSearchError] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [events, setEvents] = useState<NormalizedEventType[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string>('')
  const [paginationMeta, setPaginationMeta] = useState<PaginationType | null>(
    null
  )
  const [currentPage, setCurrentPage] = useState<number>(1)

  useEffect(() => {
    if (!config) {
      setLoading(false)
      return
    }

    fetchEvents()
  }, [config])

  async function fetchEvents(searchQuery?: string, page: number = currentPage) {
    if (!config?.url) return

    const isSearchRequest = Boolean(searchQuery)

    if (isSearchRequest) {
      setIsSearching(true)
    } else {
      setLoading(true)
    }

    setError('')

    try {
      const tagsParam = config.tags ? config.tags.join(',') : ''

      const requestUrl =
        // Construct req URL while specifying the event type
        `/api/events?apiUrl=${encodeURIComponent(config.url)}&type=${EventTypeEnum.BUON_INVITO}` +
        // Add all CMS-driven tags, if any
        (tagsParam ? `&tags=${tagsParam}` : '') +
        // Free search query, if any
        (searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : '') +
        // Add pagination parameters
        `&page=${page}`

      const response = await fetch(requestUrl)

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`)
      }

      const events = await response.json()
      setEvents(events.data || [])
      setPaginationMeta(events.meta?.pagination || null)
    } catch (err) {
      console.error('Error fetching events:', err)
      setError((err as Error).message || 'Impossibile caricare gli eventi')
      setEvents([])
    } finally {
      if (isSearchRequest) {
        setIsSearching(false)
      } else {
        setLoading(false)
      }
    }
  }

  function handleSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.currentTarget.value

    if (searchError) {
      setSearchError('')
    }

    // Real-time validation for immediate feedback
    if (!isValidSearchInputRealtime(value)) {
      setSearchError(VALIDATION_MESSAGES.INVALID_CHARS)
    }

    setSearchInput(value)
  }

  async function handleSubmit(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault()

    setSearchError('')

    // Comprehensive validation
    const validation = validateSearchInput(searchInput, {
      minLength: 2,
      maxLength: 50,
      allowedSymbols: [' ', "'", '-', '.'],
      allowAccents: true,
    })

    if (!validation.isValid) {
      setSearchError(
        validation.error || VALIDATION_MESSAGES.SANITIZATION_FAILED
      )

      // Focus back to input for accessibility
      const searchInput = document.getElementById('location-search')
      if (searchInput) {
        searchInput.focus()
      }

      return
    }

    // Perform search with sanitized input
    try {
      setSearchInput(validation.sanitizedValue)
      setCurrentPage(1)
      await fetchEvents(validation.sanitizedValue, 1)
    } catch (err) {
      console.error('Error submitting search:', err)
    }
  }

  function handleClearSearch() {
    setSearchInput('')
    setSearchError('')
    setCurrentPage(1)
    fetchEvents('', 1)
  }

  // Generate unique IDs for accessibility
  const searchInputId = 'location-search'
  const searchErrorId = 'location-search-error'
  const searchDescriptionId = 'location-search-description'

  return (
    <Section
      // `bg` is not provided by the CMS in this component,
      // but it should be; so I'm leaving this here for now.
      bg={bg as SectionBackground | undefined}
      className={`section-buoninvitoevents`}
    >
      <div className='section-buoninvitoevents__wrapper'>
        {/* TITLE */}
        <div className={`section-buoninvitoevents__title-wrapper`}>
          {openingTitle.children ? (
            <OpeningTitle
              className={`section-testimonial-slider__opening-title`}
              {...openingTitle}
            />
          ) : null}

          {titleContent ? (
            <Title
              className={`section-buoninvitoevents__title`}
              {...title}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(titleContent),
              }}
            />
          ) : null}
        </div>

        {/* CONTENT */}
        <div className='section-buoninvitoevents__content'>
          {/* Search form with improved accessibility */}
          <form
            className='section-buoninvitoevents__form'
            role='search'
            aria-label='Cerca eventi per località'
          >
            <label
              htmlFor={searchInputId}
              className='section-buoninvitoevents__form__label'
            >
              <span>Cerca l&apos;area di tuo interesse:</span>

              <span
                id={searchDescriptionId}
                className='section-buoninvitoevents__form__description'
              >
                Inserisci il nome di una città o regione italiana
              </span>
            </label>

            {/* Error UI */}
            {searchError ? (
              <InfoBanner
                icon={'Error'}
                variant={'error'}
                id={searchErrorId}
                className='section-buoninvitoevents__form__error'
                role='alert'
                aria-live='polite'
              >
                {searchError}
              </InfoBanner>
            ) : null}

            <div className='section-buoninvitoevents__form__field'>
              <div className='section-buoninvitoevents__form__input-group'>
                <input
                  id={searchInputId}
                  type='text'
                  value={searchInput}
                  onChange={handleSearchChange}
                  disabled={loading || isSearching}
                  placeholder="es. 'Veneto', 'Milano', 'Toscana'"
                  className='section-buoninvitoevents__form__input'
                  aria-describedby={`${searchDescriptionId} ${searchError ? searchErrorId : ''}`}
                  aria-invalid={searchError ? 'true' : 'false'}
                  autoComplete='off'
                  spellCheck='false'
                  maxLength={50}
                />

                {/* Clear button */}
                {searchInput ? (
                  <button
                    type='button'
                    onClick={handleClearSearch}
                    className='section-buoninvitoevents__form__clear'
                    aria-label='Cancella ricerca'
                    disabled={loading || isSearching}
                  >
                    ✕
                  </button>
                ) : null}
              </div>

              <Button
                variant='themed'
                onClick={handleSubmit}
                className='section-buoninvitoevents__form__submit'
                loading={loading || isSearching}
                disabled={loading || isSearching || !searchInput.trim()}
                aria-describedby={searchError ? searchErrorId : undefined}
              >
                Cerca
              </Button>
            </div>
          </form>

          {/* Loading state */}
          {loading ? (
            <div className='section-buoninvitoevents__loading'>
              <Spinner
                size='medium'
                color='var(--airc-ui__colors-accent-500)'
              />
            </div>
          ) : null}

          {/* Error state */}
          {error ? (
            <div className='section-buoninvitoevents__error'>
              <InfoBanner
                icon={'Error'}
                variant={'error'}
              >
                {error}
              </InfoBanner>
            </div>
          ) : null}

          {/* Events list */}
          {!loading && !error ? (
            <div className='section-buoninvitoevents__list'>
              {events?.length ? (
                events.map((event, i) => (
                  <EventCard
                    key={`evt__${i}`}
                    event={event}
                    index={i}
                  />
                ))
              ) : (
                <div className='section-buoninvitoevents__blank-slate'>
                  <InfoBanner
                    icon={'Info'}
                    variant={'info'}
                  >
                    Nessun evento trovato.
                  </InfoBanner>
                </div>
              )}
            </div>
          ) : null}

          {/* Pagination */}
          {!loading && !error && events?.length ? (
            <>
              {paginationMeta && (
                <Pagination
                  meta={paginationMeta}
                  onPageChange={(page) => {
                    setCurrentPage(page)
                    fetchEvents(searchInput, page)
                  }}
                />
              )}
            </>
          ) : null}
        </div>
      </div>
    </Section>
  )
}
